import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import { 
  Bell, 
  BellOff, 
  Check, 
  Trash2, 
  RefreshCw, 
  Settings, 
  Search,
  Mail,
  Calendar,
  User,
  Briefcase,
  MessageSquare,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Clock,
  Star,
  FileText,
  Megaphone,
  Wrench,
  Shield,
  Sparkles,
  CreditCard,
  ScrollText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { NotificationService, Notification } from '@/services/notificationService';
import { NotificationDashboard } from '@/components/NotificationDashboard';
import { getNotificationNavigationPath } from '@/utils/notificationNavigation';
import { useNavigate } from 'react-router-dom';

export default function Notifications() {
	usePageTitle("Notifications");
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stats, setStats] = useState<any>(null);

  const loadNotifications = async (pageNum = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const result = await NotificationService.getAllNotifications(pageNum, 20);
      
      if (append) {
        setNotifications(prev => [...prev, ...result.notifications]);
      } else {
        setNotifications(result.notifications);
      }
      
      // Calculate unread count from notifications
      const unreadCount = result.notifications.filter(n => !n.read_at).length;
      setUnreadCount(unreadCount);
      setHasMore(result.notifications.length === 20);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const loadStats = async () => {
    try {
      const result = await NotificationService.getNotificationStats();
      setStats(result);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const markAsRead = async (notificationIds: string[]) => {
    try {
      await NotificationService.markAsRead(notificationIds);
      
      setNotifications(prev => 
        prev.map(notification => 
          notificationIds.includes(notification.id) 
            ? { ...notification, read_at: new Date().toISOString() }
            : notification
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - notificationIds.length));
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter(n => !n.read_at)
      .map(n => n.id);
    
    if (unreadIds.length > 0) {
      await markAsRead(unreadIds);
    }
  };



  const getNotificationIcon = (type: string, data?: any) => {
    switch (type) {
      case 'job_update':
        return <Briefcase className="h-5 w-5 text-blue-500" />;
      case 'new_application':
        return <FileText className="h-5 w-5 text-green-600" />;
      case 'application_status_change':
        return <RefreshCw className="h-5 w-5 text-orange-600" />;
      case 'candidate_update':
        return <User className="h-5 w-5 text-purple-600" />;
      case 'comment_mention':
        return <MessageSquare className="h-5 w-5 text-cyan-600" />;
      case 'system':
        return getSystemNotificationIcon(data?.category, data?.priority || 'medium');
      case 'ai_processing_complete':
        return <Bell className="h-5 w-5 text-indigo-600" />;
      case 'job':
        return <Briefcase className="h-5 w-5 text-blue-500" />;
      case 'candidate':
        return <User className="h-5 w-5 text-purple-600" />;
      case 'match':
        return <Star className="h-5 w-5 text-indigo-600" />;
      case 'message':
        return <MessageSquare className="h-5 w-5 text-cyan-600" />;
      case 'calendar':
        return <Calendar className="h-5 w-5 text-blue-500" />;
      case 'alert':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'info':
        return <Info className="h-5 w-5 text-gray-600 dark:text-gray-300" />;
      default:
        return <Megaphone className="h-5 w-5 text-gray-600 dark:text-gray-300" />;
    }
  };

  const getSystemNotificationIcon = (category: string | null, priority: string) => {
    // Determine base icon by category
    let IconComponent;
    let baseColor;
    
    switch (category) {
      case 'maintenance':
        IconComponent = Wrench;
        baseColor = 'orange';
        break;
      case 'security':
        IconComponent = Shield;
        baseColor = 'red';
        break;
      case 'feature':
        IconComponent = Sparkles;
        baseColor = 'purple';
        break;
      case 'billing':
        IconComponent = CreditCard;
        baseColor = 'green';
        break;
      case 'policy':
        IconComponent = ScrollText;
        baseColor = 'blue';
        break;
      case 'general':
      default:
        IconComponent = Megaphone;
        baseColor = 'slate';
        break;
    }
    
    // Determine color intensity by priority
    let colorIntensity;
    switch (priority) {
      case 'critical':
        colorIntensity = '700';
        break;
      case 'high':
        colorIntensity = '600';
        break;
      case 'medium':
        colorIntensity = '500';
        break;
      case 'low':
        colorIntensity = '400';
        break;
      default:
        colorIntensity = '500';
        break;
    }
    
    const colorClass = `text-${baseColor}-${colorIntensity}`;
    
    return <IconComponent className={`h-5 w-5 ${colorClass}`} />;
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'text-blue-500 bg-blue-50 border-blue-200';
      case 'calendar':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'candidate':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'job':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'message':
        return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'alert':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'info':
        return 'text-cyan-600 bg-cyan-50 border-cyan-200';
      case 'match':
        return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'system':
        return 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700';
      default:
        return 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      markAsRead([notification.id]);
    }
    
    // Use the navigation utility to determine where to go
    const navigationResult = getNotificationNavigationPath(notification);
    
    if (navigationResult.handled) {
      navigate(navigationResult.path);
    }
  };



  const filteredNotifications = notifications.filter(notification => {
    // Status filter
    if (filter === 'unread' && notification.read_at) return false;
    if (filter === 'read' && !notification.read_at) return false;
    
    // Type filter
    if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        notification.title.toLowerCase().includes(query) ||
        notification.message.toLowerCase().includes(query) ||
        notification.type.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  useEffect(() => {
    loadNotifications();
    loadStats();
  }, []);

  // Infinite scroll handler
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const threshold = 100; // Load more when within 100px of bottom
    
    if (scrollHeight - scrollTop - clientHeight < threshold && hasMore && !loadingMore && !loading) {
      loadNotifications(page + 1, true);
    }
  };

  useEffect(() => {
    const channel = supabase.channel(`notifications-page-${crypto.randomUUID()}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications'
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const newNotification = payload.new as Notification;
          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);
        } else if (payload.eventType === 'UPDATE') {
          const updatedNotification = payload.new as Notification;
          setNotifications(prev => 
            prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
          );
        } else if (payload.eventType === 'DELETE') {
          const deletedNotification = payload.old as Notification;
          setNotifications(prev => 
            prev.filter(n => n.id !== deletedNotification.id)
          );
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">
              {t('notifications.title', 'Notifications')}
            </h1>
            <p className="text-muted-foreground">
              {t('notifications.subtitle', 'Manage your notifications and stay updated')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button onClick={markAllAsRead} variant="outline">
                <Check className="h-4 w-4 mr-2" />
                {t('notifications.markAllRead', 'Mark all read')}
              </Button>
            )}
            <Button onClick={() => loadNotifications(1, false)} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('notifications.refresh', 'Refresh')}
            </Button>
            <Button 
              onClick={() => window.location.href = '/settings?tab=notifications'} 
              variant="outline"
            >
              <Settings className="h-4 w-4 mr-2" />
              {t('notifications.settings', 'Notification Settings')}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <Label htmlFor="search">{t('notifications.search', 'Search')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder={t('notifications.searchPlaceholder', 'Search notifications...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>{t('notifications.status', 'Status')}</Label>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('notifications.all', 'All')}</SelectItem>
                    <SelectItem value="unread">{t('notifications.unread', 'Unread')}</SelectItem>
                    <SelectItem value="read">{t('notifications.read', 'Read')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('notifications.type', 'Type')}</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('notifications.allTypes', 'All Types')}</SelectItem>
                    <SelectItem value="job_update">{t('notifications.jobUpdate', 'Job Update')}</SelectItem>
                    <SelectItem value="new_application">{t('notifications.newApplication', 'New Application')}</SelectItem>
                    <SelectItem value="application_status_change">{t('notifications.applicationStatusChange', 'Application Status')}</SelectItem>
                    <SelectItem value="candidate_update">{t('notifications.candidateUpdate', 'Candidate Update')}</SelectItem>
                    <SelectItem value="comment_mention">{t('notifications.commentMention', 'Comment Mention')}</SelectItem>
                    <SelectItem value="system">{t('notifications.system', 'System')}</SelectItem>
                    <SelectItem value="ai_processing_complete">{t('notifications.aiProcessingComplete', 'AI Processing')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>



        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Notifications List - Takes 2/3 on large screens */}
          <div className="xl:col-span-2">
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {loading ? (
                      t('notifications.loading', 'Loading...')
                    ) : (
                      t('notifications.count', '{{count}} notifications', { count: filteredNotifications.length })
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <Button onClick={markAllAsRead} variant="outline" size="sm">
                        <Check className="h-4 w-4 mr-2" />
                        {t('notifications.markAllRead', 'Mark all read')}
                      </Button>
                    )}
                    {unreadCount > 0 && (
                      <Badge variant="secondary">
                        {t('notifications.unreadCount', '{{count}} unread', { count: unreadCount })}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div 
                  className="h-[calc(100vh-400px)] min-h-[500px] overflow-y-auto scrollbar-hide"
                  onScroll={handleScroll}
                >
                  {loading ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : filteredNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-8 text-center">
                      <BellOff className="h-16 w-16 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        {t('notifications.noNotifications', 'No notifications')}
                      </h3>
                      <p className="text-muted-foreground">
                        {t('notifications.noNotificationsDesc', 'You\'re all caught up!')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="divide-y">
                        {filteredNotifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={cn(
                              "flex items-start gap-3 p-4 cursor-pointer transition-colors",
                              !notification.read_at 
                                ? "bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-950/70" 
                                : "hover:bg-muted/50"
                            )}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="text-lg">
                              {getNotificationIcon(notification.type, notification.data)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <h4 className={cn(
                                  "text-sm leading-tight",
                                  !notification.read_at ? "font-bold" : "font-medium"
                                )}>
                                  {notification.title}
                                </h4>
                                <div className="flex items-center gap-2">
                                  {!notification.read_at && (
                                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                                  )}
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${getNotificationColor(notification.type)}`}
                                  >
                                    {notification.type.replace('_', ' ')}
                                  </Badge>
                                </div>
                              </div>
                              <p className={cn(
                                "text-sm mt-1 line-clamp-2",
                                !notification.read_at ? "text-foreground font-medium" : "text-muted-foreground"
                              )}>
                                {notification.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Loading more indicator */}
                      {loadingMore && (
                        <div className="flex items-center justify-center p-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                          <span className="ml-2 text-sm text-muted-foreground">
                            {t('notifications.loadingMore', 'Loading more...')}
                          </span>
                        </div>
                      )}
                      
                      {/* End of notifications */}
                      {!hasMore && filteredNotifications.length > 0 && (
                        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                          {t('notifications.endOfNotifications', 'End of notifications')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Analytics Sidebar - Takes 1/3 on large screens */}
          <div className="xl:col-span-1">
            <div className="sticky top-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('notifications.analytics', 'Analytics')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <NotificationDashboard />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}